// Per-meter surface class sampler from the bake's AI-classified surface map
// (meta.surface, rev >= 11). Drives ground-dependent traversal (bike grip per
// surface), the grass scatter and the terrain shader's ground materials (groundmat.js).
//
// MLOAD-CM (bake rev 91): the client never touches the whole-world
// classmap.png (14336 x 8064 px = 16 MB download, ~460 MB of canvas RGBA,
// over the iOS canvas cap). It reads two derived artifacts instead
// (pipeline/bake.py _ship_classmap_tiles; validate classmap_tiles proves
// them byte-identical to classmap.png):
//   meta.surface.coarse  {file, stride, w, h} — classmap[::stride, ::stride],
//                        the terrain zone grid (field(stride)); loaded here
//   meta.surface.tiles   {dir, tile_m, nx, ny, ext} — 1 m tiles; ensure(x, z)
//                        keeps the 3 x 3 around the player resident (LRU 16)
// classAt() on a tile that is not resident (yet) is none_id: grip factor 1
// (meadow), grass falls back to its elevation heuristic; onTile() tells the
// grass to re-scatter when one lands.
//   meta.surface.shade   {dir, tile_m, cell_m, ext, ...} (GROUND-M, bake rev 98)
//                        — per class tile, the bake's shade tile (RGB: sun
//                        visibility, the lit normal's x / z), fetched with it
//                        into the same LRU entry; onShade() when one lands.
//                        groundmat.js copies both into its near window.
//   meta.surface.ways    {dir, tile_m, cell_m, nx, ny, ext, layout, enc,
//                        look_sha, ...} (WAYS-G, bake rev 102) — per class
//                        tile, the ways field tile (waysg CONTRACT C2: 2 RGBA8
//                        planes per 1 m cell, world/pathfield.mjs ENC) shipped
//                        as RGB bands (layout rgb_bands3: rows 0..T-1 = P0.RGB,
//                        T..2T-1 = P1.RGB, 2T..3T-1 = (P0.A, P1.A, 0) — the PNG
//                        carries no alpha: a canvas premultiplies it), fetched
//                        with it the same way; the decoder repacks the bands to
//                        the two planes (T² x 4 bytes each, P0 then P1);
//                        onWays() when one lands -> groundmat's ways window.
//   FAR SLOTS (ROCK-F B) — ensureFar(list, n) keeps up to n more tiles
//                        resident, class + shade only, OUTSIDE the 3 x 3 and
//                        its LRU: the 512 m tiles the focus wedge magnifies
//                        within the relight's reach (groundmat.js picks them,
//                        its far window holds them). A far slot reports only
//                        once BOTH its class tile and its shade tile landed
//                        (onFar); no shade tile = no slot = the coarse grid,
//                        exactly as without it.
//                        A far fetch the asset queue's need score DROPPED
//                        (need.drop true: sub-pixel at the view) is not a
//                        failure: no RETRY_MS wait — the slot keeps the tile
//                        and asks the dropped part again once the view moved
//                        on (assetq needGen / onNeedView, and ensureFar while
//                        it is still wanted); one request per tile and part.
//                        A 404 / failed far tile keeps its RETRY_MS backoff.
//
// Fetch: the main thread, through the asset queue (assetq.js): the coarse grid
// and the far slots as class `bulk`, the 3 x 3 class / shade / ways tiles as
// `near` (the ground at the target). The fetched Blob goes to the decoder.
// Decode: an INLINE worker (Blob URL — esbuild ships one iife file, so no
// separate worker bundle) receives the Blob. A PNG (the class tiles + coarse
// grid, the ways tiles, rough / rockfield: EXACT data) is decoded WITHOUT a
// canvas by pngexact.js (exactPlanes, an EXACT-DATA region: Brave's canvas
// farbling flips low bits on a read-back, i.e. turns class ids into other
// ids — owner ruling 2026-10-25); a lossy colour raster (JPEG shade, WebP
// residual / s2col) still goes createImageBitmap + OffscreenCanvas
// (lossyPlanes). The R channel (RGB for a shade tile) comes back as a
// transferred Uint8Array (the two repacked RGBA planes for a ways tile); the
// main thread never sees an RGBA buffer. Without a worker, or when the worker
// reports a decode error, the same decode runs on the main thread (a PNG
// through pngexact there too, never a canvas). Bitmaps decode with
// colorSpaceConversion 'none'.

import { fetchAsset, needGen, onNeedView } from './assetq.js';
import { decodePng, isPng } from './pngexact.js';

const LRU_MAX = 16;              // resident 1 m tiles (the 3 x 3 + recent)
const RETRY_MS = 60000;          // a 404 / failed tile is refetched at most once a minute

let warned = false;
// sha256 of the bytes of pipeline/wayslook.json bundled into groundmat (the
// shader's way colours / features). The bake stamps the look it painted the
// albedo with (meta.surface.ways.look_sha); a mismatch means near and far
// draw the ways in two looks — loud, not silent (review F6c). The bundle
// cannot hash the file itself: scripts/check-ways.mjs pins this constant to
// the file's bytes (a stale constant FAILS the gate).
export const WAYSLOOK_SHA256 = 'b3bd0fd157cdf44c257b0d5fcfce5b72b648a1303156973fe8d06c22fc4f65f6';

// EXACT-DATA-BEGIN (scripts/check-ring.mjs exact:no-canvas: no canvas, bitmap or readback in here)
// a PNG (class ids, ways codes, measured fields) -> {w, h, data} for ch, through
// pngexact.js (decodePng passed in: the worker gets it stringified) — the
// samples a canvas read gave, without one: grey is R = G = B, no alpha is 255.
// Self-contained (the worker body stringifies it).
export async function exactPlanes(b, ch, decodePng) {
  const { w, h, ch: sc, data: s } = await decodePng(b);
  if (ch === sc && ch !== 2) return { w, h, data: s };
  const n = w * h, g = sc >= 3 ? 1 : 0, bb = sc >= 3 ? 2 : 0, al = sc === 4 ? 3 : sc === 2 ? 1 : -1;
  let out;
  if (ch === 8) {
    // WAYS-G rgb_bands3 (w x 3w RGB) -> P0 RGBA then P1 RGBA (w² x 4 each)
    const m = w * w;
    out = new Uint8Array(8 * m);
    if (h === 3 * w) {
      for (let i = 0; i < m; i++) {
        const a = sc * i, q = sc * (m + i), c = sc * (2 * m + i), o0 = 4 * i, o1 = 4 * (m + i);
        out[o0] = s[a]; out[o0 + 1] = s[a + g]; out[o0 + 2] = s[a + bb]; out[o0 + 3] = s[c];
        out[o1] = s[q]; out[o1 + 1] = s[q + g]; out[o1 + 2] = s[q + bb]; out[o1 + 3] = s[c + g];
      }
    }
  } else if (ch === 1 || ch === 3 || ch === 4) {
    out = new Uint8Array(n * ch);
    for (let i = 0; i < n; i++) {
      const k = sc * i, o = ch * i;
      out[o] = s[k];
      if (ch > 1) { out[o + 1] = s[k + g]; out[o + 2] = s[k + bb]; }
      if (ch === 4) out[o + 3] = al < 0 ? 255 : s[k + al];
    }
  } else throw new Error(`surface: ch ${ch}`);
  return { w, h, data: out };
}
// EXACT-DATA-END

// a lossy colour raster (JPEG shade, WebP residual / s2col: colour and light,
// never ids — a canvas read is fine here) -> {w, h, data} for ch 1 / 3 / 4.
// Self-contained (the worker body stringifies it); a document canvas only
// where there is no OffscreenCanvas (the main thread of an old Safari).
async function lossyPlanes(blob, ch) {
  if (ch === 8) throw new Error('surface: a ways tile that is not a PNG');
  let img;
  if (typeof createImageBitmap !== 'undefined') {
    img = await createImageBitmap(blob, { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  } else {
    const u = URL.createObjectURL(blob);
    try {
      img = await new Promise((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = () => reject(new Error('decode failed'));
        im.src = u;
      });
    } finally { URL.revokeObjectURL(u); }
  }
  const w = img.width, h = img.height;
  let cv;
  if (typeof OffscreenCanvas !== 'undefined') cv = new OffscreenCanvas(w, h);
  else { cv = document.createElement('canvas'); cv.width = w; cv.height = h; }
  const g = cv.getContext('2d', { willReadFrequently: true });
  g.drawImage(img, 0, 0);
  if (img.close) img.close();
  const rgba = g.getImageData(0, 0, w, h).data;
  cv.width = cv.height = 0;      // release the backing store now
  let out;
  if (ch === 4) out = new Uint8Array(rgba.buffer.slice(rgba.byteOffset, rgba.byteOffset + w * h * 4));
  else {
    out = new Uint8Array(w * h * ch);
    if (ch === 1) for (let i = 0, j = 0; i < out.length; i++, j += 4) out[i] = rgba[j];
    else for (let i = 0, j = 0; i < out.length; i += 3, j += 4) { out[i] = rgba[j]; out[i + 1] = rgba[j + 1]; out[i + 2] = rgba[j + 2]; }
  }
  return { w, h, data: out };
}

// The worker body: self-contained (stringified into a Blob, the decoders
// handed in as arguments), no module scope.
function workerMain(exact, png, lossy, isPng) {
  self.onmessage = async (e) => {
    const { id, blob, ch } = e.data;
    try {
      const b = new Uint8Array(await blob.arrayBuffer());
      const { w, h, data } = isPng(b) ? await exact(b, ch, png) : await lossy(blob, ch);
      self.postMessage({ id, status: 200, w, h, data }, [data.buffer]);
    } catch (err) {
      self.postMessage({ id, status: 0, error: String((err && err.message) || err) });
    }
  };
}

let worker;                      // undefined = not tried, null = unavailable
const pending = new Map();       // request id -> resolve
let nextId = 1;
function getWorker() {
  if (worker !== undefined) return worker;
  worker = null;
  if (typeof Worker === 'undefined') return worker;
  try {
    const src = URL.createObjectURL(new Blob([`(${workerMain.toString()})(${exactPlanes}, ${decodePng}, ${lossyPlanes}, ${isPng})`],
      { type: 'text/javascript' }));
    worker = new Worker(src);
    worker.onmessage = (e) => {
      const res = pending.get(e.data.id);
      if (res) { pending.delete(e.data.id); res(e.data); }
    };
    worker.onerror = () => {      // the worker died: fail what it holds, go main-thread
      worker = null;
      for (const res of pending.values()) res({ status: 0, error: 'worker error' });
      pending.clear();
    };
  } catch (err) {
    worker = null;
  }
  return worker;
}

// main-thread decode (fallback) — same result shape as the worker's
async function decodeMain(blob, ch) {
  const b = new Uint8Array(await blob.arrayBuffer());
  const r = isPng(b) ? await exactPlanes(b, ch, decodePng) : await lossyPlanes(blob, ch);
  return { status: 200, ...r };
}

// -> {status: 200, w, h, data} | {status: <http> | -1 network | 0 decode, error}
// | {status: 0, dropped: true, gen} (the need score dropped it: never fetched);
// never throws. A worker DECODE failure (0) retries on the main thread.
// ch = 1: the R channel (class ids); 3: RGB (shade tiles); 4: RGBA; 8: a WAYS-G
// rgb_bands3 tile (w x 3w RGB) repacked to its two RGBA planes (w² x 8 bytes).
// prio: the asset-queue class of the fetch ('near' | 'bulk').
async function decodeGrey(url, ch = 1, prio = 'near') {
  let res;
  try { res = await fetchAsset(url, { prio, as: 'blob', status: true }); } catch (err) {
    return { status: -1, error: String((err && err.message) || err) };   // network
  }
  if (!res.body) return res.dropped ? { status: 0, dropped: true, gen: res.gen } : { status: res.status };
  const blob = res.body;
  const wk = getWorker();
  if (wk) {
    const id = nextId++;
    const r = await new Promise((resolve) => {
      pending.set(id, resolve);
      wk.postMessage({ id, blob, ch });
    });
    if (r.status !== 0) return r;                  // 200
  }
  try { return await decodeMain(blob, ch); } catch (err) {
    return { status: 0, error: String((err && err.message) || err) };
  }
}

// rough.js's decode of surface/rough.png (RGB, ch 3)
export const decodeImage = decodeGrey;

export async function loadSurface(baseUrl, meta) {
  if (!meta.surface) return null;
  const s = meta.surface;
  if (!s.tiles || !s.coarse) {
    if (!warned) {
      warned = true;
      console.error(`[surface] meta.surface has no ${!s.coarse ? 'coarse' : ''}${!s.coarse && !s.tiles ? ' + ' : ''}`
        + `${!s.tiles ? 'tiles' : ''} artifact (surface/coarse.png, surface/tiles/) — this world predates `
        + 'MLOAD-CM: rebuild it (Build). Playing without the class map (meadow grip, elevation zones).');
    }
    return null;
  }
  const C = s.coarse, T = s.tiles;
  // shade tiles only on the class tiles' grid (bake rev 98+); else no relight
  const S = s.shade && s.shade.tile_m === T.tile_m && s.shade.cell_m > 0 ? s.shade : null;
  const spx = S ? Math.round(S.tile_m / S.cell_m) : 0;
  // the ways field tiles (WAYS-G) on the class tiles' grid: every bake ships
  // them (layout rgb_bands3) — missing / another layout is a broken bake
  const P = s.ways;
  if (!P || P.tile_m !== T.tile_m || !(P.cell_m > 0) || P.layout !== 'rgb_bands3') {
    throw new Error(`world meta.surface.ways missing (${P ? `layout ${P.layout}, tile_m ${P.tile_m}` : 'none'}; want rgb_bands3 on the `
      + `${T.tile_m} m class tiles): rebuild this world (Build)`);
  }
  const ppx = Math.round(P.tile_m / P.cell_m);
  // the bake's ways look vs the bundled one
  const waysLook = P.look_sha === WAYSLOOK_SHA256;
  if (waysLook === false) {
    console.error(`[surface] meta.surface.ways.look_sha ${String(P.look_sha).slice(0, 16)} is not the bundled `
      + `pipeline/wayslook.json ${WAYSLOOK_SHA256.slice(0, 16)} — the near ground draws the ways in another look `
      + 'than the baked albedo: rebuild the client or the world (Build).');
  }
  // 'near': the device painter's top ground tiles are painted from it, and the ground tree
  // draws nothing until they are (groundgen.js; 'bulk' is held from the Beam to the latch)
  const co = await decodeGrey(`${baseUrl}/${C.file}`, 1, 'near');
  if (co.status !== 200 || co.w !== C.w || co.h !== C.h) {
    console.error(`[surface] ${C.file}: ${co.status === 200 ? `${co.w}x${co.h}, meta says ${C.w}x${C.h}`
      : `status ${co.status}${co.error ? ' ' + co.error : ''}`} — playing without the class map`);
    return null;
  }
  const coarse = co.data;
  const cell = s.cell_m;
  const tpx = Math.round(T.tile_m / cell);       // cells per tile side
  const byId = new Map(s.classes.map((c) => [c.id, c]));
  // key tx + ty * nx -> {state: 'loading' | 'ready' | 'absent', data}
  // Map order = LRU order (oldest first); ensure() re-inserts what it touches
  const tiles = new Map();
  const failedAt = new Map();                    // key -> when it last failed (outlives eviction)
  const listeners = [];
  const shadeListeners = [];
  const waysListeners = [];
  const windowListeners = [];
  let lastTx = NaN, lastTy = NaN, retryAt = Infinity;
  let hitKey = -1, hitData = null;               // classAt's one-tile cache
  // far slots: index -> {key, tx, ty, cls, shade, ready} | null
  const far = [];
  const farListeners = [];
  const farFailedAt = new Map();                 // key -> when a far fetch failed
  const farNotify = (i) => {
    const f = far[i];
    for (const cb of farListeners) {
      try { cb(i, f && f.ready ? f.tx : null, f && f.ready ? f.ty : null); } catch (err) { console.error(err); }
    }
  };
  // the far fetches in flight: part ('cls' | 'shade') + key -> promise — one
  // request per tile and part, however often a slot is freed and re-wanted
  const farQ = new Map();
  const farGet = (part, tx, ty, key) => {
    const q = part + key;
    let p = farQ.get(q);
    if (!p) {
      p = (part === 'cls'
        ? inflight.get(key) || decodeGrey(`${baseUrl}/${T.dir}/${tx}_${ty}.${T.ext}`, 1, 'bulk')
        : decodeGrey(`${baseUrl}/${S.dir}/${tx}_${ty}.${S.ext}`, 3, 'bulk')).finally(() => farQ.delete(q));
      farQ.set(q, p);
    }
    return p;
  };
  // one part of far slot i's tile: a drop marks it (drop[part] = the view
  // generation) for farRedo, a 404 / failure starts the RETRY_MS backoff
  function farPart(i, rec, part) {
    rec.busy[part] = true;
    farGet(part, rec.tx, rec.ty, rec.key).then((r) => {
      rec.busy[part] = false;
      if (far[i] !== rec) return;
      if (r.dropped) { rec.drop[part] = r.gen; return; }
      rec.drop[part] = -1;
      const good = part === 'cls' ? tileOk(r) : r.status === 200 && r.w === spx && r.h === spx;
      if (!good) { farFailedAt.set(rec.key, performance.now()); return; }
      rec[part] = r.data;
      if (!rec.cls || !rec.shade) return;
      rec.ready = true;
      farNotify(i);
    });
  }
  // far slot i still holds its tile (= still wanted): ask a part the need
  // score dropped again once the view moved on (at the same view it would
  // drop again)
  function farRedo(i) {
    const rec = far[i];
    if (!rec || rec.ready) return;
    for (const part of ['cls', 'shade']) {
      if (!rec.busy[part] && rec.drop[part] >= 0 && rec.drop[part] !== needGen()) farPart(i, rec, part);
    }
  }
  function farLoad(i, tx, ty, key) {
    const rec = { key, tx, ty, cls: null, shade: null, ready: false,
      busy: { cls: false, shade: false }, drop: { cls: -1, shade: -1 } };
    far[i] = rec;
    // a resident near / LRU tile lends its bytes; else one fetch each
    const ent = tiles.get(key);
    if (ent && ent.state === 'ready' && ent.shade) {
      rec.cls = ent.data; rec.shade = ent.shade; rec.ready = true; farNotify(i); return;
    }
    farPart(i, rec, 'cls');
    farPart(i, rec, 'shade');
  }
  // a re-score (a new view): the held slots ask their dropped parts again
  if (S) onNeedView(() => { for (let i = 0; i < far.length; i++) farRedo(i); });

  function evict() {
    while (tiles.size > LRU_MAX) {
      const k = tiles.keys().next().value;
      tiles.delete(k);
      if (k === hitKey) { hitKey = -1; hitData = null; }
    }
  }
  const inflight = new Map();                    // key -> decode promise (one fetch per tile)
  const tileOk = (r) => r.status === 200 && r.w === tpx && r.h === tpx;
  function load(tx, ty, key) {
    const ent = { state: 'loading', data: null, shade: null, ways: null };
    tiles.delete(key);                           // a retried tile: newest in LRU order
    tiles.set(key, ent);
    let p = inflight.get(key);
    if (!p) {
      p = decodeGrey(`${baseUrl}/${T.dir}/${tx}_${ty}.${T.ext}`).then((r) => {
        inflight.delete(key);
        if (tileOk(r)) failedAt.delete(key);
        else {                                   // recorded even if evicted meanwhile
          const t = performance.now();
          failedAt.set(key, t);
          retryAt = Math.min(retryAt, t + RETRY_MS);
          if (r.status !== 404) console.warn(`[surface] tile ${tx}_${ty}: status ${r.status} ${r.error || ''}`);
        }
        return r;
      });
      inflight.set(key, p);
    }
    // the shade tile rides along: best effort, no retry (no relight there)
    if (S) {
      decodeGrey(`${baseUrl}/${S.dir}/${tx}_${ty}.${S.ext}`, 3).then((r) => {
        if (tiles.get(key) !== ent || r.status !== 200 || r.w !== spx || r.h !== spx) return;
        ent.shade = r.data;
        for (const cb of shadeListeners) {
          try { cb(tx, ty); } catch (err) { console.error(err); }
        }
      });
    }
    // the ways field tile rides along the same way (one per class tile; until
    // it lands, or when its fetch fails, no ways layer there)
    decodeGrey(`${baseUrl}/${P.dir}/${tx}_${ty}.${P.ext}`, 8).then((r) => {
      if (tiles.get(key) !== ent || r.status !== 200 || r.w !== ppx || r.h !== 3 * ppx) return;
      ent.ways = r.data;
      for (const cb of waysListeners) {
        try { cb(tx, ty); } catch (err) { console.error(err); }
      }
    });
    p.then((r) => {
      if (tiles.get(key) !== ent) return;        // evicted (or reloaded) while in flight
      if (!tileOk(r)) { ent.state = 'absent'; return; }   // 404 / decode error -> none_id
      ent.state = 'ready';
      ent.data = r.data;
      for (const cb of listeners) {
        try { cb(tx, ty); } catch (err) { console.error(err); }
      }
    });
  }

  return {
    meta: s,
    ways: P,    // the ways tile meta (WAYS-G)
    waysLook,   // the bake's ways look_sha == the bundled wayslook
    classAt(x, z) {
      const cx = Math.floor(x / cell), cz = Math.floor(z / cell);
      if (cx < 0 || cz < 0 || cx >= s.w || cz >= s.h) return s.none_id;
      const tx = Math.floor(cx / tpx), ty = Math.floor(cz / tpx);
      const key = tx + ty * T.nx;
      if (key !== hitKey) {
        const ent = tiles.get(key);
        if (!ent || ent.state !== 'ready') return s.none_id;
        hitKey = key;
        hitData = ent.data;
      }
      return hitData[(cz - ty * tpx) * tpx + (cx - tx * tpx)];
    },
    at(x, z) {
      return byId.get(this.classAt(x, z)) || null;
    },
    // The raw class-id grid at `stride`, for the terrain shader's coarse
    // class texture (terrain.setSurfaceClasses -> groundmat). Only the
    // shipped coarse stride exists. copy false: the held grid itself (the
    // caller only reads it — groundgen's coarse classes: no per-world copy)
    field(stride, copy = true) {
      if (stride !== C.stride) {
        throw new Error(`surface.field(${stride}): only the bake's coarse stride ${C.stride} is shipped`);
      }
      return { data: copy ? coarse.slice() : coarse, w: C.w, h: C.h, cell: cell * C.stride };
    },
    // Keep the 3 x 3 tiles around (x, z) resident. Per frame: a no-op while
    // the player stays in the same tile (and no failed tile is due a retry).
    ensure(x, z) {
      const tx = Math.floor(x / cell / tpx), ty = Math.floor(z / cell / tpx);
      if (tx === lastTx && ty === lastTy && performance.now() < retryAt) return;
      const ox = lastTx, oy = lastTy;              // the previous 3 x 3 (NaN at first)
      lastTx = tx;
      lastTy = ty;
      retryAt = Infinity;
      const now = performance.now();
      const entered = [];
      for (let j = ty - 1; j <= ty + 1; j++) {
        if (j < 0 || j >= T.ny) continue;
        for (let i = tx - 1; i <= tx + 1; i++) {
          if (i < 0 || i >= T.nx) continue;
          const key = i + j * T.nx;
          const ent = tiles.get(key);
          const failed = failedAt.get(key);
          const wait = (!ent || ent.state === 'absent') && failed !== undefined && now - failed < RETRY_MS;
          if (wait) retryAt = Math.min(retryAt, failed + RETRY_MS);
          if (ent && (ent.state !== 'absent' || wait)) {
            tiles.delete(key);                     // touch: newest in LRU order
            tiles.set(key, ent);
            // still resident from an earlier visit: its near-window slot
            // (groundmat, tile mod 3) may hold another tile by now
            if (ent.state === 'ready' && !(Math.abs(i - ox) <= 1 && Math.abs(j - oy) <= 1)) entered.push(i, j);
          } else if (!wait) {
            load(i, j, key);
          }
        }
      }
      evict();
      for (let k = 0; k < entered.length; k += 2) {
        for (const cb of windowListeners) {
          try { cb(entered[k], entered[k + 1]); } catch (err) { console.error(err); }
        }
      }
    },
    // the 3 x 3's centre tile (NaN before the first ensure)
    nearTile() { return [lastTx, lastTy]; },
    // FAR SLOTS: keep `list` ([[tx, ty], ...], first n) in the n far slots —
    // a kept tile stays in its slot, a dropped one frees it (onFar(i, null)),
    // a new one loads into a free slot. No shade tiles in the bake: no-op.
    ensureFar(list, n) {
      if (!S) return;
      const now = performance.now();
      const want = new Map();
      for (const [tx, ty] of list) {
        if (want.size >= n) break;
        if (tx < 0 || ty < 0 || tx >= T.nx || ty >= T.ny) continue;
        const key = tx + ty * T.nx, f = farFailedAt.get(key);
        if (f !== undefined && now - f < RETRY_MS) continue;
        want.set(key, [tx, ty]);
      }
      for (let i = 0; i < n; i++) {
        if (far[i] && !want.has(far[i].key)) {
          const was = far[i].ready;
          far[i] = null;
          if (was) farNotify(i);
        } else farRedo(i);                       // kept: a dropped part asks again
      }
      for (const f of far) if (f) want.delete(f.key);
      for (const [key, [tx, ty]] of want) {
        let i = 0;
        while (i < n && far[i]) i++;
        if (i >= n) break;
        farLoad(i, tx, ty, key);
      }
    },
    // cb(i, tx, ty) when far slot i holds tile (tx, ty) with its shade, cb(i,
    // null) when it is freed
    onFar(cb) { farListeners.push(cb); },
    // a far slot's bytes {cls, shade, tpx, spx} once it reported, else null
    farData(i) {
      const f = far[i];
      return f && f.ready ? { cls: f.cls, shade: f.shade, tpx, spx } : null;
    },
    // cb(tx, ty) when a tile that stayed resident re-enters the 3 x 3 (no
    // fetch, so no onTile / onShade / onWays): groundmat re-copies its slot
    onWindow(cb) { windowListeners.push(cb); },
    // cb(tx, ty) after a tile becomes resident (grass re-scatters)
    onTile(cb) { listeners.push(cb); },
    // cb(tx, ty) after a tile's shade tile landed (groundmat near window)
    onShade(cb) { shadeListeners.push(cb); },
    // cb(tx, ty) after a tile's ways field tile landed (groundmat ways window)
    onWays(cb) { waysListeners.push(cb); },
    // a resident tile's bytes: {cls (tpx², class ids), shade (spx² x 3 RGB
    // or null), ways (ppx² x 8: plane P0 RGBA then P1 RGBA, or null), tpx,
    // spx, ppx} — null while it is not resident
    tileData(tx, ty) {
      if (tx < 0 || ty < 0 || tx >= T.nx || ty >= T.ny) return null;
      const ent = tiles.get(tx + ty * T.nx);
      if (!ent || ent.state !== 'ready') return null;
      return { cls: ent.data, shade: ent.shade, ways: ent.ways, tpx, spx, ppx };
    },
  };
}
